import React, { useEffect, useState, useMemo } from "react";
import { Modal } from "@mui/material";
import { useRecoilState } from "recoil";
import { isMissingBillingAddress } from "../Profile/helpers";
import { IntermediateSuccessConfirmation } from "./IntermediateSuccessConfirmation";
import { FinalSuccessConfirmation } from "./FinalSuccessConfirmation";
import { patientProfileState } from "../../recoil/atoms";
import { InsuranceUploadForm } from "../Insurance/InsuranceUploadForm";
import { AddressForm } from "../Address/AddressForm";
import { ModalStep } from "./hooks/useModalAnalytics";
import { CenteredModalContainer } from "../shared/Modal/Modal.styles";

// Concise conditional for step transitions
const getNextStep = (
  current: ModalStep,
  needsBillingAddress: boolean,
): ModalStep => {
  if (current === ModalStep.Insurance && needsBillingAddress) {
    return ModalStep.IntermediateSuccess;
  }
  if (current === ModalStep.IntermediateSuccess && needsBillingAddress) {
    return ModalStep.Address;
  }
  return ModalStep.FinalSuccess;
};

export const MissingBillingInformationModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  missingInsurance?: boolean;
  missingAddress?: boolean;
}> = ({ isOpen, onClose, missingInsurance = true, missingAddress = false }) => {
  const [patientInfo] = useRecoilState(patientProfileState);
  // Memoize initial step calculation
  const initialStep = useMemo((): ModalStep => {
    if (missingInsurance && missingAddress) {
      return ModalStep.Insurance; // Need both, start with insurance
    }
    if (missingInsurance && !missingAddress) {
      return ModalStep.Insurance; // Only need insurance
    }
    if (!missingInsurance && missingAddress) {
      return ModalStep.Address; // Only need address
    }
    return ModalStep.FinalSuccess; // Nothing missing (shouldn't happen)
  }, [missingInsurance, missingAddress]);

  const [currentStep, setCurrentStep] = useState<ModalStep>(initialStep);

  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(initialStep);
    }
  }, [isOpen, initialStep]);

  // Check if billing address is missing (used in multiple places)
  const needsBillingAddress = isMissingBillingAddress(patientInfo);

  // Handlers using concise conditional
  const handleInsuranceComplete = () => {
    setCurrentStep(getNextStep(ModalStep.Insurance, needsBillingAddress));
  };

  const handleInsuranceSuccessContinue = () => {
    setCurrentStep(
      getNextStep(ModalStep.IntermediateSuccess, needsBillingAddress),
    );
  };

  const handleAddressComplete = () => {
    setCurrentStep(getNextStep(ModalStep.Address, needsBillingAddress));
  };

  // Render step
  const renderCurrentStep = () => {
    switch (currentStep) {
      case ModalStep.Insurance:
        return (
          <InsuranceUploadForm
            onComplete={handleInsuranceComplete}
            onCancel={onClose}
            missingInsurance={missingInsurance}
          />
        );
      case ModalStep.IntermediateSuccess:
        return (
          <IntermediateSuccessConfirmation
            onPrimaryAction={handleInsuranceSuccessContinue}
            onSecondaryAction={onClose}
          />
        );
      case ModalStep.Address:
        return (
          <AddressForm onComplete={handleAddressComplete} onCancel={onClose} />
        );
      case ModalStep.FinalSuccess:
        return <FinalSuccessConfirmation onPrimaryAction={onClose} />;
      default:
        return null;
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <CenteredModalContainer>{renderCurrentStep()}</CenteredModalContainer>
    </Modal>
  );
};
